import React, { useState, useEffect, useCallback } from 'react';
import client from '../../api/client';
import AdminTable from '../../components/Admin/AdminTable';
import ExcelUploadModal from '../../components/Admin/ExcelUploadModal';

const SAMPLE_COLS = ['warehouse_code','device_name','part_code','model','serial_number','status'];
const STATUSES = ['available','reserved','faulty'];
const EMPTY_FORM = { warehouse:'', device_name:'', part_code:'', model:'', serial_number:'', status:'available', notes:'' };

const TABLE_COLS = [
  { key:'device_name', label:'Device Name' },
  { key:'part_code', label:'Part Code' },
  { key:'model', label:'Model' },
  { key:'serial_number', label:'Serial No' },
  { key:'warehouse_name', label:'Warehouse' },
  { key:'ticket_number', label:'Used In Ticket', render: v => v
    ? <span className="font-mono text-xs font-semibold text-purple-600">{v}</span>
    : <span className="text-gray-300 text-xs">—</span>
  },
  { key:'status', label:'Status', render: v => {
    const c = { available:'bg-green-100 text-green-700', reserved:'bg-amber-100 text-amber-700', faulty:'bg-red-100 text-red-600' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c[v] || 'bg-gray-100 text-gray-600'}`}>{v}</span>;
  }},
];

export default function SpareUnits() {
  const [data, setData] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [units, whs] = await Promise.all([
        client.get('/admin/spare-units/'),
        client.get('/admin/warehouses/'),
      ]);
      setData(Array.isArray(units.data) ? units.data : units.data.results ?? []);
      setWarehouses(Array.isArray(whs.data) ? whs.data : whs.data.results ?? []);
    } catch { setError('Failed to load spare units.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditRow(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (row) => { setEditRow(row); setForm({ ...row, warehouse: row.warehouse ?? '' }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editRow) await client.put(`/admin/spare-units/${editRow.id}/`, form);
      else await client.post('/admin/spare-units/', form);
      setShowForm(false); load();
    } catch (err) { alert(err?.response?.data?.detail || 'Save failed.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await client.delete(`/admin/spare-units/${deleteRow.id}/`); setDeleteRow(null); load(); }
    catch { alert('Delete failed.'); }
  };

  const handleUnreserve = async (row) => {
    if (!window.confirm(`Unreserve "${row.device_name}" (${row.serial_number || 'no S/N'})?`)) return;
    try {
      await client.post(`/inventory/spare-units/${row.id}/unreserve/`);
      load();
    } catch (err) { alert(err?.response?.data?.detail || 'Unreserve failed.'); }
  };

  const handleUpload = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data: res } = await client.post('/admin/upload/spare-units/', fd);
    load(); return res;
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = 'border border-gray-200 rounded-xl px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-purple-300';

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Individual Spare Units</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track individual spare units by serial number</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowUpload(true)} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 font-medium">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            Upload Excel
          </button>
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            Add Unit
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <AdminTable
          columns={TABLE_COLS} data={data} loading={loading}
          onEdit={openEdit} onDelete={setDeleteRow}
          extraActions={(row) => row.status === 'reserved' ? (
            <button
              onClick={() => handleUnreserve(row)}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 transition-colors"
              title="Mark as available again"
            >
              Unreserve
            </button>
          ) : null}
        />
      </div>

      {showUpload && <ExcelUploadModal title="Spare Units" onUpload={handleUpload} onClose={() => setShowUpload(false)} sampleColumns={SAMPLE_COLS} templateEntity="spare-units" />}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800">{editRow ? 'Edit Spare Unit' : 'Add Spare Unit'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Warehouse *</label>
                <select className={inp} value={form.warehouse} onChange={e => set('warehouse', e.target.value)} required>
                  <option value="">Select warehouseâ€¦</option>
                  {warehouses.map(w => <option key={w.id} value={w.id}>{w.name} ({w.code})</option>)}
                </select>
              </div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Device Name *</label><input className={inp} value={form.device_name} onChange={e => set('device_name', e.target.value)} required /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Part Code</label><input className={inp} value={form.part_code} onChange={e => set('part_code', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Model</label><input className={inp} value={form.model} onChange={e => set('model', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Serial Number</label><input className={inp} value={form.serial_number} onChange={e => set('serial_number', e.target.value)} /></div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Status</label>
                <select className={inp} value={form.status} onChange={e => set('status', e.target.value)}>
                  {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-medium text-gray-600 mb-1 block">Notes</label>
                <textarea className={`${inp} resize-none`} rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm rounded-xl bg-[#C1278D] hover:bg-[#a01f75] text-white font-semibold disabled:opacity-50">{saving ? 'Savingâ€¦' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-base font-semibold text-gray-800">Delete Spare Unit</h3>
            <p className="text-sm text-gray-600">Are you sure you want to delete <strong>{deleteRow.device_name}</strong> (S/N: {deleteRow.serial_number})?</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteRow(null)} className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

